/**
 * Form Registry - Central registry for all forms in the application
 * This allows MenuManager to discover and link to available forms
 * Created: 1405/02/17
 *
 * ⚠️ AGENT INSTRUCTION:
 * هر بار که یک Route جدید با hasParams: false به App.tsx اضافه شد،
 * باید یک entry متناظر در این فایل (formRegistry) هم اضافه شود.
 * فرم‌هایی که hasParams: true دارند (نیاز به ID در URL) را اضافه نکنید.
 */

import { FormDefinition } from '../menu/types';

// Import all available forms
// Each form should have a corresponding entry here

export const formRegistry: FormDefinition[] = [
  // Dashboard & General
  {
    id: 'Dashboard',
    name: 'داشبورد',
    description: 'صفحه داشبورد اصلی سیستم',
    category: 'general',
    defaultRoute: '/dashboard',
    hasParams: false,
    icon: 'dashboard'
  },
  
  // Project Management
  {
    id: 'ProjectList',
    name: 'لیست پروژه‌ها',
    description: 'نمایش لیست کامل پروژه‌ها',
    category: 'projects',
    defaultRoute: '/projects',
    hasParams: false,
    icon: 'business'
  },
  {
    id: 'ProjectNew',
    name: 'پروژه جدید',
    description: 'ایجاد پروژه جدید',
    category: 'projects',
    defaultRoute: '/projects/new',
    hasParams: false,
    icon: 'add_business'
  },
  {
    id: 'ProjectDetail',
    name: 'جزئیات پروژه',
    description: 'نمایش جزئیات یک پروژه',
    category: 'projects',
    defaultRoute: '/projects/:id',
    hasParams: true,
    icon: 'business'
  },
  {
    id: 'WBSManagement',
    name: 'مدیریت WBS',
    description: 'ساختار شکست کار پروژه',
    category: 'projects',
    defaultRoute: '/projects/:projectId/wbs',
    hasParams: true,
    icon: 'account_tree'
  },
  {
    id: 'Documents',
    name: 'مستندات پروژه',
    description: 'مدیریت مستندات و فایل‌های پروژه',
    category: 'projects',
    defaultRoute: '/projects/:projectId/documents',
    hasParams: true,
    icon: 'folder'
  },
  {
    id: 'ImageDocumentation',
    name: 'مستندات تصویری',
    description: 'مدیریت تصاویر پروژه',
    category: 'projects',
    defaultRoute: '/projects/:projectId/images',
    hasParams: true,
    icon: 'photo_library'
  },
  {
    id: 'VideoDocumentation',
    name: 'مستندات ویدیویی',
    description: 'مدیریت ویدیوهای پروژه',
    category: 'projects',
    defaultRoute: '/projects/:projectId/videos',
    hasParams: true,
    icon: 'video_library'
  },
  {
    id: 'Comments',
    name: 'نظرات پروژه',
    description: 'نظرات و مکالمات پروژه',
    category: 'projects',
    defaultRoute: '/projects/:projectId/comments',
    hasParams: true,
    icon: 'comment'
  },
  {
    id: 'ProjectMembers',
    name: 'اعضای پروژه',
    description: 'مدیریت اعضای تیم پروژه',
    category: 'projects',
    defaultRoute: '/projects/:projectId/members',
    hasParams: true,
    icon: 'group'
  },
  {
    id: 'Reports',
    name: 'گزارشات پروژه',
    description: 'گزارشات مرتبط با پروژه‌ها',
    category: 'projects',
    defaultRoute: '/reports',
    hasParams: false,
    icon: 'assessment'
  },
  {
    id: 'MyTasks',
    name: 'کارتابل من',
    description: 'لیست وظایف کاربر جاری',
    category: 'projects',
    defaultRoute: '/my-tasks',
    hasParams: false,
    icon: 'playlist_add_check'
  },
  {
    id: 'Profile',
    name: 'پروفایل کاربری',
    description: 'مشاهده و ویرایش پروفایل کاربر',
    category: 'general',
    defaultRoute: '/profile',
    hasParams: false,
    icon: 'person'
  },
  
  // HR & Payroll
  {
    id: 'EmployeeList',
    name: 'لیست پرسنل',
    description: 'نمایش لیست کارمندان',
    category: 'hr',
    defaultRoute: '/payroll/employees',
    hasParams: false,
    icon: 'people'
  },
  {
    id: 'EmployeeDetail',
    name: 'جزئیات پرسنل',
    description: 'نمایش جزئیات یک کارمند',
    category: 'hr',
    defaultRoute: '/payroll/employees/:id',
    hasParams: true,
    icon: 'people'
  },
  {
    id: 'Employers',
    name: 'کارفرمایان',
    description: 'مدیریت کارفرمایان',
    category: 'hr',
    defaultRoute: '/payroll/employers',
    hasParams: false,
    icon: 'business'
  },
  {
    id: 'Workplaces',
    name: 'محل خدمت/پروژه',
    description: 'مدیریت محل‌های خدمت/پروژه',
    category: 'hr',
    defaultRoute: '/payroll/workplaces',
    hasParams: false,
    icon: 'location_city'
  },
  {
    id: 'Attendance',
    name: 'حضور و غیاب',
    description: 'ثبت و مدیریت حضور و غیاب پرسنل',
    category: 'hr',
    defaultRoute: '/payroll/attendance',
    hasParams: false,
    icon: 'event_available'
  },
  {
    id: 'PayrollProcessing',
    name: 'محاسبه حقوق',
    description: 'پردازش و محاسبه حقوق ماهانه',
    category: 'payroll',
    defaultRoute: '/payroll/processing',
    hasParams: false,
    icon: 'payments'
  },
  {
    id: 'Loans',
    name: 'کسورات حقوق',
    description: 'مدیریت کسورات حقوق پرسنل (وام، مساعده، بیمه تکمیلی و سایر موضوع‌ها)',
    category: 'payroll',
    defaultRoute: '/payroll/loans',
    hasParams: false,
    icon: 'account_balance_wallet'
  },
  {
    id: 'PayrollList',
    name: 'لیست حقوق',
    description: 'لیست محاسبات حقوق پرسنل',
    category: 'payroll',
    defaultRoute: '/payroll/payroll-list',
    hasParams: false,
    icon: 'list_alt'
  },
  {
    id: 'ContractReport',
    name: 'گزارش قراردادهای پرسنلی',
    description: 'گزارش قراردادها و احکام پرسنلی',
    category: 'payroll',
    defaultRoute: '/payroll/reports/contracts',
    hasParams: false,
    icon: 'receipt'
  },
  {
    id: 'ContractReportNew',
    name: 'احکام حقوقی',
    description: 'گزارش احکام حقوقی و دستمزد پرسنل',
    category: 'payroll',
    defaultRoute: '/reports/payroll-decrees',
    hasParams: false,
    icon: 'description'
  },
  {
    id: 'Currencies',
    name: 'مدیریت ارزها',
    description: 'مدیریت ارزهای سیستم حقوق و دستمزد',
    category: 'payroll',
    defaultRoute: '/payroll/currencies',
    hasParams: false,
    icon: 'currency_exchange'
  },
  {
    id: 'PayrollConfiguration',
    name: 'پیکربندی حقوق',
    description: 'تنظیمات سیستم حقوق و دستمزد',
    category: 'payroll',
    defaultRoute: '/payroll/config',
    hasParams: false,
    icon: 'settings'
  },

  // Expenses
  {
    id: 'Expenses',
    name: 'هزینه‌ها',
    description: 'مدیریت هزینه‌های پروژه',
    category: 'expenses',
    defaultRoute: '/expenses',
    hasParams: false,
    icon: 'money_off'
  },
  {
    id: 'ExpenseTypes',
    name: 'انواع هزینه',
    description: 'مدیریت انواع هزینه‌ها',
    category: 'expenses',
    defaultRoute: '/expenses/types',
    hasParams: false,
    icon: 'category'
  },
  {
    id: 'ExpenseGroups',
    name: 'گروه‌های هزینه',
    description: 'مدیریت گروه‌بندی هزینه‌ها',
    category: 'expenses',
    defaultRoute: '/expenses/groups',
    hasParams: false,
    icon: 'folder_special'
  },
  {
    id: 'Departments',
    name: 'دپارتمان‌ها',
    description: 'مدیریت دپارتمان‌ها برای جذب هزینه‌های عمومی',
    category: 'expenses',
    defaultRoute: '/expenses/departments',
    hasParams: false,
    icon: 'business'
  },
  
  // Finance
  {
    id: 'Persons',
    name: 'شخص‌ها',
    description: 'مدیریت اشخاص (پرسنل، مشتریان، تامین‌کنندگان)',
    category: 'finance',
    defaultRoute: '/finance/persons',
    hasParams: false,
    icon: 'person'
  },
  {
    id: 'BankGroups',
    name: 'گروه‌های بانکی',
    description: 'مدیریت گروه‌بندی حساب‌های بانکی',
    category: 'finance',
    defaultRoute: '/finance/bank-groups',
    hasParams: false,
    icon: 'account_balance'
  },
  {
    id: 'BankAccounts',
    name: 'حساب‌های بانکی',
    description: 'مدیریت حساب‌های بانکی',
    category: 'finance',
    defaultRoute: '/finance/bank-accounts',
    hasParams: false,
    icon: 'account_balance'
  },
  {
    id: 'BankTypes',
    name: 'انواع بانک',
    description: 'مدیریت انواع بانک‌ها',
    category: 'finance',
    defaultRoute: '/finance/bank-types',
    hasParams: false,
    icon: 'account_balance'
  },
  {
    id: 'Transactions',
    name: 'تراکنش‌ها',
    description: 'مدیریت تراکنش‌های مالی',
    category: 'finance',
    defaultRoute: '/finance/transactions',
    hasParams: false,
    icon: 'swap_horiz'
  },
  {
    id: 'Checks',
    name: 'چک‌ها',
    description: 'مدیریت چک‌ها',
    category: 'finance',
    defaultRoute: '/finance/checks',
    hasParams: false,
    icon: 'money'
  },
  {
    id: 'CostCenters',
    name: 'مراکز هزینه',
    description: 'مدیریت مراکز هزینه',
    category: 'finance',
    defaultRoute: '/finance/cost-centers',
    hasParams: false,
    icon: 'location_on'
  },
  {
    id: 'FieldsOfStudy',
    name: 'رشته‌های تحصیلی',
    description: 'مدیریت رشته‌های تحصیلی',
    category: 'finance',
    defaultRoute: '/finance/fields-of-study',
    hasParams: false,
    icon: 'school'
  },
  {
    id: 'MilitaryStatuses',
    name: 'وضعیت نظام‌وظیفه',
    description: 'مدیریت وضعیت‌های نظام‌وظیفه',
    category: 'finance',
    defaultRoute: '/finance/military-statuses',
    hasParams: false,
    icon: 'shield'
  },

  // Accounting
  {
    id: 'ChartOfAccounts',
    name: 'سرفصل حساب‌ها',
    description: 'مدیریت سرفصل حساب‌های حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/chart-of-accounts',
    hasParams: false,
    icon: 'account_tree'
  },
  {
    id: 'FiscalYears',
    name: 'سال‌های مالی',
    description: 'مدیریت سال‌های مالی',
    category: 'accounting',
    defaultRoute: '/accounting/fiscal-years',
    hasParams: false,
    icon: 'calendar_today'
  },
  {
    id: 'Vouchers',
    name: 'اسناد حسابداری',
    description: 'مدیریت اسناد حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/vouchers',
    hasParams: false,
    icon: 'receipt'
  },
  {
    id: 'FinancialReports',
    name: 'گزارش‌های حسابداری',
    description: 'گزارش‌های مالی و حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/financial-reports',
    hasParams: false,
    icon: 'assessment'
  },
  {
    id: 'DetailCodes',
    name: 'کدهای تفصیلی',
    description: 'تعریف و مدیریت کدهای تفصیلی حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/detail-codes',
    hasParams: false,
    icon: 'playlist_add'
  },
  {
    id: 'FloatingCenters',
    name: 'مراکز شناور',
    description: 'تعریف گروه‌ها و مراکز شناور برای تخصیص تراکنش‌های حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/floating-centers',
    hasParams: false,
    icon: 'hub'
  },

  // Contractors
  {
    id: 'Contracts',
    name: 'قراردادها',
    description: 'لیست و مدیریت قراردادها',
    category: 'contractors',
    defaultRoute: '/contracts',
    hasParams: false,
    icon: 'description'
  },
  {
    id: 'ContractDashboards',
    name: 'داشبورد قراردادها',
    description: 'داشبورد و خلاصه وضعیت قراردادها',
    category: 'contractors',
    defaultRoute: '/contractors/contract-dashboards',
    hasParams: false,
    icon: 'dashboard'
  },
  {
    id: 'ContractProgresses',
    name: 'پیشرفت قراردادها',
    description: 'گزارش پیشرفت قراردادها',
    category: 'contractors',
    defaultRoute: '/contract-progresses',
    hasParams: false,
    icon: 'trending_up'
  },
  {
    id: 'ContractTypes',
    name: 'انواع قرارداد',
    description: 'مدیریت انواع قراردادها',
    category: 'contractors',
    defaultRoute: '/contractors/contract-types',
    hasParams: false,
    icon: 'category'
  },
  {
    id: 'DeductionTypes',
    name: 'انواع کسورات قرارداد',
    description: 'مدیریت انواع کسورات قراردادها',
    category: 'contractors',
    defaultRoute: '/contractors/deduction-types',
    hasParams: false,
    icon: 'remove_circle'
  },

  // Accounting
  {
    id: 'ChartOfAccounts',
    name: 'کدینگ حسابداری',
    description: 'مدیریت سرفصل‌های حسابداری (کدینگ چهار سطحی)',
    category: 'accounting',
    defaultRoute: '/accounting/chart-of-accounts',
    hasParams: false,
    icon: 'account_tree'
  },
  {
    id: 'FiscalYears',
    name: 'سال‌های مالی',
    description: 'مدیریت سال‌های مالی و تنظیمات حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/fiscal-years',
    hasParams: false,
    icon: 'calendar_today'
  },
  {
    id: 'Vouchers',
    name: 'اسناد حسابداری',
    description: 'ثبت و مدیریت اسناد حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/vouchers',
    hasParams: false,
    icon: 'receipt'
  },
  {
    id: 'FinancialReports',
    name: 'گزارشات مالی',
    description: 'تراز آزمایشی، ترازنامه، سود و زیان',
    category: 'accounting',
    defaultRoute: '/accounting/financial-reports',
    hasParams: false,
    icon: 'bar_chart'
  },
  {
    id: 'TrialBalance',
    name: 'تراز آزمایشی',
    description: 'تراز آزمایشی سلسله‌مراتبی — از سطح گروه حساب تا تفصیلی',
    category: 'accounting',
    defaultRoute: '/accounting/trial-balance',
    hasParams: false,
    icon: 'balance'
  },
  {
    id: 'LegalBooks',
    name: 'دفاتر قانونی',
    description: 'دفتر روزنامه قانونی — خروجی قالب سامانه دفاتر تجاری الکترونیکی (مودیان)',
    category: 'accounting',
    defaultRoute: '/accounting/legal-books',
    hasParams: false,
    icon: 'menu_book'
  },
  {
    id: 'DetailCodes',
    name: 'کدهای تفصیلی',
    description: 'مدیریت کدهای تفصیلی حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/detail-codes',
    hasParams: false,
    icon: 'list'
  },
  {
    id: 'FloatingCenters',
    name: 'مراکز شناور',
    description: 'مدیریت گروه‌ها و مراکز شناور (مراکز هزینه/درآمد)',
    category: 'accounting',
    defaultRoute: '/accounting/floating-centers',
    hasParams: false,
    icon: 'hub'
  },
  {
    id: 'AccountingMappings',
    name: 'مپینگ حسابداری',
    description: 'مدیریت مپینگ موجودیت‌ها و آیتم‌های حقوق به سرفصل‌های حسابداری',
    category: 'accounting',
    defaultRoute: '/accounting/mappings',
    hasParams: false,
    icon: 'account_balance_wallet'
  },

  // System & Admin
  {
    id: 'UserManagement',
    name: 'مدیریت کاربران',
    description: 'مدیریت کاربران سیستم',
    category: 'admin',
    defaultRoute: '/users',
    hasParams: false,
    icon: 'people',
    requiredRole: 'Admin'
  },
  {
    id: 'MenuManagement',
    name: 'مدیریت منوها',
    description: 'پیکربندی منوی سیستم',
    category: 'admin',
    defaultRoute: '/settings/menu',
    hasParams: false,
    icon: 'menu',
    requiredRole: 'Admin'
  },
  {
    id: 'CompanySettings',
    name: 'تنظیمات شرکت',
    description: 'مدیریت تنظیمات شرکت (نام، کد اقتصادی، لوگو، موقعیت مکانی و غیره)',
    category: 'admin',
    defaultRoute: '/settings/company',
    hasParams: false,
    icon: 'business'
  },
  {
    id: 'TourManagement',
    name: 'مدیریت تورها',
    description: 'مدیریت راهنمای تعاملی کاربران',
    category: 'admin',
    defaultRoute: '/tour-management',
    hasParams: false,
    icon: 'help',
    requiredRole: 'Admin'
  },
  {
    id: 'RoleManagement',
    name: 'مدیریت نقش‌ها',
    description: 'مدیریت نقش‌ها و دسترسی‌های کاربران',
    category: 'admin',
    defaultRoute: '/admin/roles',
    hasParams: false,
    icon: 'admin_panel_settings',
    requiredRole: 'Admin'
  },
  {
    id: 'Search',
    name: 'جستجو',
    description: 'جستجوی پیشرفته در سیستم',
    category: 'general',
    defaultRoute: '/search',
    hasParams: false,
    icon: 'search'
  },
  {
    id: 'Alerts',
    name: 'اعلان‌ها',
    description: 'لیست اعلانات و هشدارها',
    category: 'general',
    defaultRoute: '/alerts',
    hasParams: false,
    icon: 'notifications'
  },

  // File Sharing
  {
    id: 'FileSharing',
    name: 'اشتراک‌گذاری فایل',
    description: 'مدیریت فایل‌ها و اشتراک‌گذاری',
    category: 'general',
    defaultRoute: '/file-sharing',
    hasParams: false,
    icon: 'cloud'
  },

  // Inventory / Sales / Production / Reports
  {
    id: 'InventoryItems',
    name: 'کالاها',
    description: 'اطلاعات پایه کالا',
    category: 'inventory',
    defaultRoute: '/inventory/items',
    hasParams: false,
    icon: 'inventory_2'
  },
  {
    id: 'InventoryWarehouses',
    name: 'انبارها',
    description: 'انبارها و محل‌های انبار',
    category: 'inventory',
    defaultRoute: '/inventory/warehouses',
    hasParams: false,
    icon: 'warehouse'
  },
  {
    id: 'InventoryBaseData',
    name: 'اطلاعات پایه انبار',
    description: 'گروه کالا، واحدها، انواع انبار، رانندگان، نوع فرم‌ها و نگاشت حساب‌ها',
    category: 'inventory',
    defaultRoute: '/inventory/base-data',
    hasParams: false,
    icon: 'inventory'
  },
  {
    id: 'InventoryDocuments',
    name: 'اسناد انبار',
    description: 'رسید و حواله انبار',
    category: 'inventory',
    defaultRoute: '/inventory/documents',
    hasParams: false,
    icon: 'receipt_long'
  },
  {
    id: 'SalesInvoices',
    name: 'فاکتورها',
    description: 'فاکتور فروش/خرید/مرجوعی',
    category: 'sales',
    defaultRoute: '/sales/invoices',
    hasParams: false,
    icon: 'point_of_sale'
  },
  {
    id: 'SalesBaseData',
    name: 'اطلاعات پایه فروش',
    description: 'خدمات، ویزیتورها، کسورات و طرح‌ها',
    category: 'sales',
    defaultRoute: '/sales/base-data',
    hasParams: false,
    icon: 'sell'
  },
  {
    id: 'ProductionBoms',
    name: 'فرمول‌های تولید',
    description: 'فرمول ساخت (BOM)',
    category: 'production',
    defaultRoute: '/production/boms',
    hasParams: false,
    icon: 'account_tree'
  },
  {
    id: 'ProductionBatches',
    name: 'دستورهای ساخت',
    description: 'دستور ساخت و گردش تولید',
    category: 'production',
    defaultRoute: '/production/batches',
    hasParams: false,
    icon: 'precision_manufacturing'
  },
  {
    id: 'ReportCardex',
    name: 'کاردکس کالا',
    description: 'کاردکس تراکنش‌های کالا',
    category: 'reports',
    defaultRoute: '/reports/cardex',
    hasParams: false,
    icon: 'table_view'
  },
  {
    id: 'ReportStockBalance',
    name: 'مانده انبار',
    description: 'گزارش مانده انبار',
    category: 'reports',
    defaultRoute: '/reports/stock-balance',
    hasParams: false,
    icon: 'inventory'
  },
  {
    id: 'ReportSales',
    name: 'گزارش جامع فروش',
    description: 'گزارش فروش با فیلترهای متنوع',
    category: 'reports',
    defaultRoute: '/reports/sales',
    hasParams: false,
    icon: 'assessment'
  },
  {
    id: 'StockOrders',
    name: 'سفارش انبار',
    description: 'سفارش خرید/فروش/ساخت',
    category: 'inventory',
    defaultRoute: '/inventory/orders',
    hasParams: false,
    icon: 'shopping_cart'
  },
  {
    id: 'CountDocs',
    name: 'انبارگردانی',
    description: 'اسناد شمارش موجودی',
    category: 'inventory',
    defaultRoute: '/inventory/count-docs',
    hasParams: false,
    icon: 'fact_check'
  },
  {
    id: 'SalesOrders',
    name: 'سفارش‌های فروش',
    description: 'سفارش فروش و پیش‌فاکتور',
    category: 'sales',
    defaultRoute: '/sales/orders',
    hasParams: false,
    icon: 'list_alt'
  },
  {
    id: 'ProdSerials',
    name: 'سری‌های ساخت',
    description: 'سری ساخت و قیمت‌گذاری سه‌سطحی',
    category: 'production',
    defaultRoute: '/production/serials',
    hasParams: false,
    icon: 'qr_code'
  },
  {
    id: 'Posting',
    name: 'ارسال به حسابداری',
    description: 'سندسازی اسناد انبار و فاکتورها',
    category: 'accounting',
    defaultRoute: '/posting',
    hasParams: false,
    icon: 'send'
  },
  {
    id: 'ModuleSettings',
    name: 'تنظیمات انبار/فروش',
    description: 'تنظیمات ماژول انبار و فروش',
    category: 'admin',
    defaultRoute: '/settings/modules',
    hasParams: false,
    icon: 'settings'
  },
  {
    id: 'ReportVisitorCommission',
    name: 'گزارش پورسانت ویزیتور',
    description: 'پورسانت ویزیتورها در بازه‌ی زمانی',
    category: 'reports',
    defaultRoute: '/reports/visitor-commission',
    hasParams: false,
    icon: 'assessment'
  },
  {
    id: 'ReportProduction',
    name: 'گزارش دستورهای ساخت',
    description: 'وضعیت، پیشرفت و بهای دستورهای ساخت',
    category: 'reports',
    defaultRoute: '/reports/production',
    hasParams: false,
    icon: 'precision_manufacturing'
  },
];

// Helper functions
export const getFormDefinition = (formId: string): FormDefinition | undefined => {
  return formRegistry.find(form => form.id === formId);
};

export const getAllForms = (): FormDefinition[] => {
  return formRegistry;
};

export const getFormsByCategory = (category: string): FormDefinition[] => {
  return formRegistry.filter(form => form.category === category);
};

export const getFormCategories = (): string[] => {
  const categories = new Set(formRegistry.map(form => form.category).filter((c): c is string => Boolean(c)));
  return Array.from(categories);
};

export const hasForm = (formId: string): boolean => {
  return formRegistry.some(form => form.id === formId);
};

// Get form route (replace params if provided)
export const getFormRoute = (formId: string, params?: Record<string, string>): string => {
  const form = getFormDefinition(formId);
  if (!form) return '#';
  
  let route = form.defaultRoute || '#';
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      route = route.replace(`:${key}`, value);
    });
  }
  
  return route;
};

// Extension for required role
declare module '../menu/types' {
  interface FormDefinition {
    requiredRole?: string;
  }
}
